<template>
  <div>
    <UPage>
      <UPageHeader class="mt-16 !border-b-0 bg-workbook-warmgray">
        <UContainer>
        <ContentRenderer v-if="page" :value="page" />
        </UContainer>
      </UPageHeader>
      <UPageBody class="!mt-12">
        
        <UContainer>

          <!-- MODE FILTER (synkad till store via lokal ref) -->
          <ClientOnly>
            <URadioGroup orientation="horizontal" variant="list" v-model="mode" :items="workbookModeOptions"
              class="mb-6" />
          </ClientOnly>
        </UContainer>
        <!-- Filter + Export -->
        <UContainer class="my-2" v-motion-slide-top :delay="1000">
          <div class="flex gap-4 flex-wrap">
            <USlideover :close="{
              color: 'primary',
              variant: 'outline',
              class: 'rounded-full'
            }" title="Workbook Filters" description="Filtering the current product display"
              :side="isMobile ? 'left' : 'top'">
              <UButton class="p-0 cursor-pointer" label="Filters" color="neutral" variant="link"
                trailing-icon="i-lucide-plus" />
              <template #body>
                <UContainer>
                  <div v-if="isLoading" class="p-6 text-center text-gray-500">
                    Loading...
                  </div>
                  <div v-else class="lg:flex gap-20">
                    <template v-for="key in filterKeys" :key="key">
                      <div class="flex flex-col mb-4 lg:mb-0">
                        <p class="font-semibold mb-2">{{ filterLabels[key] }}</p>
                        <div v-if="['category', 'gender', 'productPrimaryUse', 'productFit'].includes(key)"
                          class="flex lg:flex-col flex-wrap gap-2">
                          <UCheckbox v-for="val in dynamicOptions[key]" :key="val" :label="labelFor(key, val)"
                            :model-value="filters[key].includes(val)"
                            @update:model-value="checked => toggleFilter(key, val, checked)" />
                        </div>
                        <div v-else-if="key === 'status'" class="flex flex-wrap gap-2 lg:flex-col">
                          <UCheckbox v-for="val in statusOptions" :key="val" :label="val"
                            :model-value="filters.status.includes(val)"
                            @update:model-value="checked => toggleFilter('status', val, checked)" />
                        </div>
                      </div>
                    </template>
                  </div>
                  <UButton label="Clear all filters" color="neutral" variant="link" class="mt-12 cursor-pointer"
                    @click="clearFilters" />
                </UContainer>
              </template>
            </USlideover>
            <template v-if="isHydrated">
              <template v-for="af in activeFilters" :key="af.key + af.val">
                <UButton :label="chipLabel(af.key as FilterKey, af.val)" color="primary" variant="solid" icon="i-lucide-x"
                  @click="removeFilter(af.key as FilterKey, af.val)" />
              </template>
            </template>
            <UDropdownMenu class="ml-auto" :items="exportMenuItems">
              <UButton class="cursor-pointer" :label="isExporting ? 'Exporting…' : 'PDF Export'" color="primary"
                variant="link" trailing-icon="workbook:pdf" :disabled="isExporting" :loading="isExporting" />
            </UDropdownMenu>
          </div>
        </UContainer>
        <UContainer>
        </UContainer>
        <!-- Innehåll -->
        <div>
          <div v-if="isLoading" class="py-24 text-center text-lg text-gray-400">
            Loading products...
          </div>
          <div v-else-if="filteredCategories.length === 0" class="py-24 text-center text-lg text-gray-400">
            No products matching your filters
          </div>
          <div v-else>
            <UContainer>
              <!-- Bara LineOverview - kategori-rubrik och länk renderas där -->
              <WorkbookLineOverview :categories="filteredCategories" />
            </UContainer>
          </div>
        </div>
      </UPageBody>
    </UPage>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useCatalogStore } from '@/store/catalog'
import type { CatalogItem, CategoryModel, ProductModel } from '@/types/catalog'
import { useFilterStorage } from '@/composables/useFilterStorage'
import { useFilterQuerySync } from '@/composables/useFilterQuerySync'
import { usePdfExport } from '@/composables/usePdfExport'
import WorkbookLineOverview from '@/components/workbook/LineOverview.vue'
import { useWindowSize, watchDebounced } from '@vueuse/core'
import { useCartStore } from '@/store/cart'
import { useAuthStore } from '@/store/auth'
import { useSettingsStore } from '@/store/settings'
import { useNotesStore } from '@/store/notes'

const settings = useSettingsStore()
settings.hydrate()

const isHydrated = ref(false)
onMounted(() => {
  isHydrated.value = true
})

const { width } = useWindowSize()
const isMobile = computed(() => width.value < 767)

const { data: page } = await useAsyncData('lineOverview', () =>
  queryCollection('lineOverview').first()
)

const catalogStore = useCatalogStore()
const authStore = useAuthStore()
const cartStore = useCartStore()
const notesStore = useNotesStore()
notesStore.hydrate()

const workbookModeOptions = computed(() => {
  const opts = [{ value: 'All', label: 'Show all' }]
  for (const cat of catalogStore.topCategories) {
    opts.push({ value: cat, label: cat })
  }
  return opts
})

const mode = ref(settings.workbookMode || 'All')
watch(mode, (val) => {
  if (val !== settings.workbookMode) settings.setWorkbookMode(val)
})
watch(() => settings.workbookMode, (val) => {
  if (val !== mode.value) mode.value = val
})
watchDebounced(workbookModeOptions, (opts, prev) => {
  const values = opts.map(o => o.value)
  const prevValues = prev?.map(o => o.value) || []
  if (values.length === prevValues.length && values.every((v, i) => v === prevValues[i])) return
  const valueSet = new Set(values)
  if (!valueSet.has(mode.value)) mode.value = 'All'
  if (!valueSet.has(settings.workbookMode)) settings.setWorkbookMode('All')
}, { debounce: 50, maxWait: 200 })

// Filterkonfiguration
const filterKeys = [
  'category',
  'gender',
  'productPrimaryUse',
  'productFit',
  'status'
] as const
type FilterKey = (typeof filterKeys)[number]

const filterLabels: Record<FilterKey, string> = {
  category: 'Category',
  gender: 'Gender',
  productPrimaryUse: 'Product Primary Use',
  productFit: 'Product Fit',
  status: 'Status'
}

const statusOptions = ['New', 'Revised', 'New Colour', 'With notes'] as const

const statusCheckers: Record<string, (v: CatalogItem) => boolean> = {
  'New': v => !!v.New,
  'Revised': v => !!v.Revised,
  'New Colour': v => !!v.NewColour,
  'With notes': v => notesStore.hasNoteForStyle(v.Style)
}
const isLoading = computed(
  () => catalogStore.isLoading || !catalogStore.items.length
)

const { filters, reset } = useFilterStorage(
  'line-overview-filters-v3',
  filterKeys
)
useFilterQuerySync(filters, filterKeys, {
  shouldIgnoreQuery: (key, value) => {
    if (key !== 'category') return false
    return catalogStore.topCategories.some(cat => cat.toLowerCase() === value.toLowerCase())
  }
})

const dynamicOptions = computed(() => {
  const cats: CategoryModel[] = catalogStore.categoriesByTopCategory(mode.value)
  const categories = cats.map(c => c.Name)
  const allVariants = cats.flatMap(cat => cat.ProductModels.flatMap(m => m.variants))
  const getUnique = (field: keyof CatalogItem) =>
    Array.from(new Set(allVariants.map(i => i[field]).filter(Boolean))) as string[]
  return {
    category: Array.from(new Set(categories)).sort(),
    gender: getUnique('Gender'),
    productPrimaryUse: getUnique('ProductPrimaryUse'),
    productFit: getUnique('ProductFit'),
    status: statusOptions
  }
})

function toggleFilter(key: FilterKey, val: string, checked: boolean) {
  const arr = filters.value[key]
  if (checked && !arr.includes(val)) arr.push(val)
  else if (!checked) filters.value[key] = arr.filter(v => v !== val)
}
function removeFilter(key: FilterKey, val: string) {
  if (!(key in filters.value)) return
  filters.value[key] = filters.value[key].filter(
    v => v !== val
  )
}
function clearFilters() {
  reset()
}

const activeFilters = computed(() =>
  Object.entries(filters.value)
    .filter(([, arr]) => arr.length)
    .flatMap(([k, arr]) => arr.map(v => ({ key: k, val: v })))
)

function variantPasses(variant: CatalogItem): boolean {
  if (
    filters.value.gender.length &&
    variant.Gender !== undefined && !filters.value.gender.includes(variant.Gender)
  )
    return false
  if (
    filters.value.productPrimaryUse.length &&
    variant.ProductPrimaryUse !== undefined && !filters.value.productPrimaryUse.includes(variant.ProductPrimaryUse)
  )
    return false
  if (
    filters.value.productFit.length &&
    variant.ProductFit !== undefined && !filters.value.productFit.includes(variant.ProductFit)
  )
    return false
  if (filters.value.status.length) {
    const ok = filters.value.status.some(lbl => statusCheckers[lbl]?.(variant))
    if (!ok) return false
  }
  return true
}
function modelIncluded(model: ProductModel): boolean {
  return model.variants.some(variantPasses)
}

const filteredCategories = computed<CategoryModel[]>(() => {
  if (isLoading.value) return []
  const baseCats: CategoryModel[] = catalogStore.categoriesByTopCategory(mode.value)
  const out: CategoryModel[] = []
  for (const cat of baseCats) {
    if (filters.value.category.length && !filters.value.category.includes(cat.Name)) {
      continue
    }
    const keptModels: ProductModel[] = []
    for (const m of cat.ProductModels) {
      if (modelIncluded(m)) keptModels.push(m)
    }
    if (keptModels.length) out.push({ ...cat, ProductModels: keptModels })
  }
  return out
})

const { isExporting, exportLineOverview, exportWorkbook } = usePdfExport()
const savedStyles = computed(() => Array.from(cartStore.savedStyles))
const selectedProductStyles = computed(() => savedStyles.value)
const exportMenuItems = computed(() => {
  const hasFilters = activeFilters.value.length > 0
  const hasSelectedProducts = savedStyles.value.length > 0
  const items = [
    [{ type: 'label', label: 'Line Overview to PDF:' }],
    [
      {
        label: 'Complete Line Overview: ' + mode.value,
        disabled: isExporting.value,
        onSelect: () =>
          exportLineOverview({
            size: 'A1',
            orientation: 'portrait',
            filters: {},
            mode: mode.value
          })
      },
      {
        label: 'Using current filters',
        disabled: isExporting.value || !hasFilters,
        onSelect: () =>
          exportLineOverview({
            size: 'A1',
            orientation: 'portrait',
            filters: filters.value,
            mode: mode.value
          })
      },

      {
        label: 'Selected products',
        disabled: isExporting.value || !hasSelectedProducts,
        onSelect: () =>
          exportLineOverview({
            size: 'A1',
            orientation: 'portrait',
            filters: { styles: selectedProductStyles.value },
            mode: mode.value
          })
      }
    ],
  ]
  items.push([{ type: 'label', label: 'Workbook to PDF:' }])
  items.push([
    {
      label: 'Complete workbook: ' + mode.value,
      disabled: isExporting.value,
      onSelect: () => exportWorkbook({ all: true, useFilters: false, filters: undefined, categories: undefined, mode: mode.value })
    },
    {
      label: 'Using current filters',
      disabled: isExporting.value || !hasFilters,
      onSelect: () =>
        exportWorkbook({ useFilters: true, filters: filters.value, all: false, categories: undefined, mode: mode.value })
    },

    {
      label: 'Selected products only',
      disabled: isExporting.value || !hasSelectedProducts,
      onSelect: () =>
        exportWorkbook({ styles: selectedProductStyles.value, mode: mode.value })
    },
    {
      label: 'Current Cart',
      disabled: isExporting.value || !hasSelectedProducts,
      onSelect: () =>
        exportWorkbook({ styles: selectedProductStyles.value, cart: true, mode: mode.value })
    }
  ])
  return items
})

function labelFor(key: string, val: string) {
  if (key === 'category') return val
  return val
}
function chipLabel(key: FilterKey, val: string) {
  return labelFor(key, val)
}

useContentSeo(page, {
  title: 'Kudo Sales demo – Products',
  description: 'Kudo Sales demo',
})
</script>
